import React, { useState, useRef, useEffect } from 'react'
import AxiosInstance from 'utils/AxiosInstance'

const questions = [
  'What is your name?',
  'How old are you?',
  'Where do you live?',
]

export default function Interview() {
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState([])
  const [transcribing, setTranscribing] = useState(false)
  const [evaluation, setEvaluation] = useState(null)
  const [isRecording, setIsRecording] = useState(false)
  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state !== 'inactive') {
        mediaRecorderRef.current?.stop()
      }
    }
  }, [])

  const playQuestion = async (text) => {
    return new Promise((resolve) => {
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.onend = resolve
      speechSynthesis.speak(utterance)
    })
  }

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          sampleSize: 16,
          echoCancellation: true,
          noiseSuppression: true,
        },
      })

      // Use WebM format which is supported by Whisper
      const options = {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 16000,
      }

      mediaRecorderRef.current = new MediaRecorder(stream, options)
      mediaRecorderRef.current.stream = stream // Store for cleanup
      audioChunksRef.current = []

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data)
        }
      }

      mediaRecorderRef.current.start(100) // Collect data every 100ms
      setIsRecording(true)
    } catch (error) {
      console.error('Recording error:', error)
      throw error
    }
  }

  const stopRecording = () => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current) {
        resolve(null)
        return
      }

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, {
          type: 'audio/webm;codecs=opus', // Explicit supported format
        })

        // Clean up media resources
        mediaRecorderRef.current.stream
          .getTracks()
          .forEach((track) => track.stop())
        mediaRecorderRef.current = null
        audioChunksRef.current = []

        resolve(blob)
      }

      mediaRecorderRef.current.stop()
      setIsRecording(false)
    })
  }

  const transcribeAudio = async (audioBlob) => {
    setTranscribing(true)
    const formData = new FormData()
    formData.append('audio', audioBlob, `answer_${step}.webm`)

    try {
      const response = await AxiosInstance.post(
        '/recruitment/interview/transcribe',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      )
      return response.data.text
    } finally {
      setTranscribing(false)
    }
  }

  const handleStartQuestion = async () => {
    try {
      // Play the question
      await playQuestion(questions[step])

      // Start recording automatically
      await startRecording()
    } catch (error) {
      console.error('Error starting question:', error)
      alert('Error starting recording. Please check microphone permissions.')
    }
  }

  const handleSubmitAnswer = async () => {
    try {
      // Stop recording
      const audioBlob = await stopRecording()

      // Transcribe answer
      const text = await transcribeAudio(audioBlob)
      setAnswers((prev) => [...prev, text])

      // Move to next question or finish
      if (step + 1 < questions.length) {
        setStep(step + 1)
      } else {
        const gradeResponse = await AxiosInstance.post(
          '/recruitment/interview/grade',
          { answers: [...answers, text] }
        )
        setEvaluation(gradeResponse.data.evaluation)
      }
    } catch (error) {
      console.error('Error submitting answer:', error)
      alert('Error processing your answer. Please try again.')
    }
  }

  if (evaluation) {
    return (
      <div className='interview-container'>
        <h2>Interview Complete</h2>
        <div className='evaluation'>
          <h3>Evaluation:</h3>
          <pre>{evaluation}</pre>
        </div>
      </div>
    )
  }

  return (
    <div className='interview-container'>
      <h2>
        Question {step + 1} of {questions.length}
      </h2>
      <p className='question-text'>{questions[step]}</p>

      <div className='controls'>
        {!isRecording ? (
          <button onClick={handleStartQuestion} disabled={transcribing}>
            {transcribing ? 'Processing...' : 'Start Question'}
          </button>
        ) : (
          <button onClick={handleSubmitAnswer} className='stop-button'>
            Submit Answer
          </button>
        )}
      </div>

      <div className='answers'>
        <h3>Your Answers:</h3>
        <ol>
          {answers.map((a, i) => (
            <li key={i}>{a || 'No transcript available'}</li>
          ))}
        </ol>
      </div>
    </div>
  )
}
